/**
 * DataQualityReviewPage.tsx  (DataQualityReviewPage)
 * Rule review & approval panel with tabbed categories.
 * Redesigned — uses shared GenWizard light theme.
 * Business logic is 100% preserved; only visuals changed.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench, faCheckCircle, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Modal, Form, Table } from 'react-bootstrap';           // keep Bootstrap only where needed for complex widgets

import { T, cls, injectTheme } from '../theme/theme';
import { DQ_RULE_CAT_LABELS } from '../constants';
import DQRuleTuningAssistant from '../assistant/DQRuleTuningAssistant';

// ─── Types (unchanged) ────────────────────────────────────────────────────────
type FilterStatus = 'All' | 'Draft' | 'Approved' | 'Rejected';

type Rule = {
  id?: string; rule_id?: string; act?: string; sev?: string;
  d?: string; vc?: string; fix?: string; bi?: string; cat?: string;
  lifecycle?: FilterStatus; vl?: string; td?: string; ve?: any;
  ex?: any[]; exv?: any[]; [k: string]: any;
};

type ValidationResult = {
  rule_id: string; action: string; description: string;
  dimension: string; lifecycle: string; status: string;
  fail_count: number; passed_count: number; total_records: number;
  sample_failures?: any[];
  examples?: { affected_count?: number; sample_changes?: any[] };
  [k: string]: any;
};

type Insight = {
  modeling_recommendations?: any[];
  validation_rules?: Rule[];
  validation_results: ValidationResult[];
  dim_rollups: Record<string, any>;
};

type State = {
  insight: Insight;
  filterStatus: FilterStatus;
  activeTab: string;
  expandedRuleId: string | null;
  editingRuleId: string | null;
  editedRule: Partial<Rule>;
  editingModelingIndex: number | null;
  editedModeling: any;
  showLookupModal: boolean;
  lookupModalData?: { columnName?: string; lkpPairs?: any[]; selectedLookup?: any; recIndex?: number };
  showRuleTuning: boolean;
  tuningContext: any | null;
};

interface DQReviewProps {
  selectedApp: any;
  dataModelData?: any;
  profilingData: any;
  lkpPairData: any;
  onInsightUpdate?: (newInsight: any) => void;
  onShouldBeLookupUpdate: (selectedLookup: any) => void;
}

const FILTER_OPTIONS: FilterStatus[] = ['All', 'Draft', 'Approved', 'Rejected'];

const SEV_META: Record<string, { label: string; cls: string }> = {
  H: { label: 'High',   cls: 'gw-badge-red' },
  M: { label: 'Medium', cls: 'gw-badge-amber' },
  L: { label: 'Low',    cls: 'gw-badge-green' },
};

const LC_META: Record<string, string> = {
  Approved: 'gw-badge-green',
  Rejected: 'gw-badge-red',
  Draft:    'gw-badge-muted',
};

const safeRender = (v: any): string => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

// ─── Component ────────────────────────────────────────────────────────────────
export default class DataQualityReviewPage extends React.Component<DQReviewProps, State> {

  constructor(props: DQReviewProps) {
    super(props);
    const insight: Insight = props.profilingData?.insight || {
      modeling_recommendations: [], validation_rules: [], validation_results: [], dim_rollups: {},
    };
    const firstTab = this.resolveFirstTab(insight, props.selectedApp?.tool);
    this.state = {
      insight,
      filterStatus: 'All',
      activeTab: firstTab,
      expandedRuleId: null,
      editingRuleId: null,
      editedRule: {},
      editingModelingIndex: null,
      editedModeling: {},
      showLookupModal: false,
      lookupModalData: undefined,
      showRuleTuning: false,
      tuningContext: null,
    };
  }

  componentDidMount() { injectTheme(); }

  componentDidUpdate(prevProps: DQReviewProps) {
    if (prevProps.profilingData?.insight !== this.props.profilingData?.insight) {
      this.setState({ insight: this.props.profilingData.insight });
    }
  }

  resolveFirstTab(insight: Insight, tool?: string): string {
    if (tool === 'BUILD_MDM_USING_DATA') return 'modeling';
    const cats = [...new Set((insight.validation_rules || []).map(r => r.cat || 'other'))];
    return cats[0] || 'modeling';
  }

  getUniqueId(rule: Rule): string {
    return rule.rule_id || rule.id || JSON.stringify(rule);
  }

  // ── Rule lifecycle actions ─────────────────────────────────────────────────
  setRuleLifecycle(uniqueId: string, lifecycle: FilterStatus) {
    this.setState(prev => {
      const updatedInsight: Insight = {
        ...prev.insight,
        validation_rules: (prev.insight.validation_rules || []).map(r =>
          this.getUniqueId(r) === uniqueId ? { ...r, lifecycle } : r
        ),
      };
      return { insight: updatedInsight };
    }, () => this.props.onInsightUpdate?.(this.state.insight));
  }

  handleApprove = (id: string) => this.setRuleLifecycle(id, 'Approved');
  handleReject  = (id: string) => this.setRuleLifecycle(id, 'Rejected');

  handleEdit = (id: string) => {
    const rule = (this.state.insight.validation_rules || []).find(r => this.getUniqueId(r) === id);
    if (rule) this.setState({ editingRuleId: id, editedRule: { ...rule } });
  };

  handleSaveEdit = () => {
    const { editedRule } = this.state;
    const editId = this.getUniqueId(editedRule as Rule);
    this.setState(prev => ({
      insight: {
        ...prev.insight,
        validation_rules: (prev.insight.validation_rules || []).map(r =>
          this.getUniqueId(r) === editId ? { ...r, ...editedRule } : r
        ),
      },
      editingRuleId: null, editedRule: {},
    }));
  };

  // ── Modeling actions ───────────────────────────────────────────────────────
  handleApproveModeling = (index: number) => {
    const updated = [...(this.state.insight.modeling_recommendations || [])];
    const rec = updated[index];
    if (!rec) return;
    if (rec.tag === 'should_be_lookup') {
      const columnName = this.props.profilingData?.column_name;
      const lkpPairs = [
        ...(this.props.lkpPairData?.certain_lookup_pairs || []),
        ...(this.props.lkpPairData?.possible_lookup_pairs || []),
      ].sort((a: any, b: any) => (a.rdsLabel || '').localeCompare(b.rdsLabel || ''));
      const selectedLookup = lkpPairs.find((l: any) => l.code === columnName);
      this.setState({ showLookupModal: true, lookupModalData: { columnName, lkpPairs, selectedLookup, recIndex: index } });
    } else {
      updated[index] = { ...rec, approved: true };
      this.setState({ insight: { ...this.state.insight, modeling_recommendations: updated } });
    }
  };

  handleRejectModeling = (index: number) => {
    const updated = [...(this.state.insight.modeling_recommendations || [])];
    if (updated[index]) {
      updated.splice(index, 1);
      this.setState({ insight: { ...this.state.insight, modeling_recommendations: updated } });
    }
  };

  handleEditModeling = (index: number) => {
    const rec = (this.state.insight.modeling_recommendations || [])[index];
    if (rec) this.setState({ editingModelingIndex: index, editedModeling: { ...rec } });
  };

  handleSaveEditModeling = () => {
    const updated = [...(this.state.insight.modeling_recommendations || [])];
    if (this.state.editingModelingIndex != null) {
      updated[this.state.editingModelingIndex] = { ...this.state.editedModeling };
      this.setState({
        insight: { ...this.state.insight, modeling_recommendations: updated },
        editingModelingIndex: null, editedModeling: {},
      });
    }
  };

  updateRuleInTable = (acceptedRule: any) => {
    this.setState(prev => {
      const rules = [...(prev.insight.validation_rules || [])];
      const id = acceptedRule.rule_id || acceptedRule.id;
      const idx = rules.findIndex(r => (r.rule_id || r.id) === id);
      if (idx >= 0) rules[idx] = { ...rules[idx], ...acceptedRule };
      else rules.push({ ...acceptedRule, lifecycle: 'Draft' });
      return { insight: { ...prev.insight, validation_rules: rules } };
    }, () => this.props.onInsightUpdate?.(this.state.insight));
  };

  handleRuleAccept = (rule: any) => this.updateRuleInTable(rule);

  // ── Tab helpers ────────────────────────────────────────────────────────────
  buildTabs() {
    const { insight } = this.state;
    const catCounts: Record<string, number> = (insight.validation_rules || []).reduce(
      (acc: Record<string, number>, r) => { const k = r.cat || 'other'; acc[k] = (acc[k] || 0) + 1; return acc; }, {}
    );
    const catKeys = Object.keys(catCounts).sort((a, b) => {
      const ia = Object.keys(DQ_RULE_CAT_LABELS).indexOf(a);
      const ib = Object.keys(DQ_RULE_CAT_LABELS).indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1; if (ib === -1) return -1;
      return ia - ib;
    });
    const tabs = [];
    if (this.props.selectedApp?.tool === 'BUILD_MDM_USING_DATA') {
      tabs.push({ key: 'modeling', label: 'Modeling', count: insight.modeling_recommendations?.length || 0, icon: '🔧' });
    }
    catKeys.forEach(k => tabs.push({ key: k, label: DQ_RULE_CAT_LABELS[k] || k, count: catCounts[k], icon: '✅' }));
    return tabs;
  }

  // ── Filter pill row ────────────────────────────────────────────────────────
  renderFilterBar(catKey: string) {
    const { insight, filterStatus } = this.state;
    const rules = (insight.validation_rules || []).filter(r => (r.cat || 'other') === catKey);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {FILTER_OPTIONS.map(opt => {
          const count = rules.filter(r => opt === 'All' || (r.lifecycle || 'Draft') === opt).length;
          const active = filterStatus === opt;
          return (
            <button key={opt}
              className={cls('gw-filter-pill', active && 'active')}
              onClick={() => this.setState({ filterStatus: opt })}>
              {opt}
              <span className="gw-pill-count">{count}</span>
            </button>
          );
        })}
        {filterStatus !== 'All' && (
          <button className="gw-btn gw-btn-ghost gw-btn-sm"
            onClick={() => this.setState({ filterStatus: 'All' })}>
            Reset
          </button>
        )}
      </div>
    );
  }

  // ── Single rule card ───────────────────────────────────────────────────────
  renderRuleCard(rule: Rule, catKey: string) {
    const { insight, expandedRuleId, editingRuleId, editedRule } = this.state;
    const uniqueId  = this.getUniqueId(rule);
    const isOpen    = expandedRuleId === uniqueId;
    const isEditing = editingRuleId  === uniqueId;
    const sev       = SEV_META[rule.sev || ''] || { label: 'Unknown', cls: 'gw-badge-muted' };
    const lcCls     = LC_META[rule.lifecycle || 'Draft'] || 'gw-badge-muted';
    const vrs       = (insight.validation_results || []).filter(vr => vr.rule_id === (rule.rule_id || rule.id));

    return (
      <div key={uniqueId} className="gw-card" style={{ marginBottom: 10 }}>

        {/* ── Accordion header ── */}
        <div
          style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}
          onClick={() => this.setState({ expandedRuleId: isOpen ? null : uniqueId })}
        >
          <FontAwesomeIcon
            icon={isOpen ? faChevronDown : faChevronRight}
            style={{ fontSize: 10, color: T.textMuted, marginTop: 4, flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: T.textPrimary }}>{rule.id || rule.rule_id || '—'}</span>
              <span className={cls('gw-badge', lcCls)}>{rule.lifecycle || 'Draft'}</span>
              <span className={cls('gw-badge', sev.cls)}>{sev.label}</span>
              {rule.act && <span className="gw-badge gw-badge-brand">{rule.act}</span>}
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.4 }}>{rule.d || ''}</div>
          </div>
        </div>

        {/* ── Expanded body ── */}
        {isOpen && (
          <div style={{ borderTop: `1px solid ${T.border}`, padding: '14px 16px' }}>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <button className="gw-btn gw-btn-success gw-btn-sm" onClick={() => this.handleApprove(uniqueId)}>✓ Approve</button>
              <button className="gw-btn gw-btn-outline gw-btn-sm" onClick={() => this.handleEdit(uniqueId)}>✎ Edit</button>
              <button className="gw-btn gw-btn-danger gw-btn-sm" onClick={() => this.handleReject(uniqueId)}>✖ Reject</button>
              <button className="gw-btn gw-btn-ghost gw-btn-sm"
                onClick={() => {
                  const f = this.props.profilingData;
                  const summary = {
                    column_name: f.column_name, orig_column_name: f.orig_column_name, source_prefix: f.source_prefix,
                    field_profile: { data_type: f.data_type, count: f.count, not_null_count: f.not_null_count,
                      null_count: f.null_count, density: f.data_density,
                      distinct_values: Object.entries(f.distinct_values || {}).slice(0, 15) },
                  };
                  this.setState({ showRuleTuning: true, tuningContext: { type: 'rule', fieldObj: summary, ruleObj: rule } });
                }}>
                🧠 Tune Rule
              </button>
            </div>

            {/* Editable or read-only fields */}
            {(['d', 'fix', 'bi'] as const).map(field => {
              const labels: Record<string, string> = { d: 'Description', fix: 'Correction Suggestion', bi: 'Business Impact' };
              return (
                <div key={field} style={{ marginBottom: 12 }}>
                  <div className="gw-label" style={{ marginBottom: 4 }}>{labels[field]}</div>
                  {isEditing ? (
                    <textarea
                      className="gw-textarea"
                      rows={2}
                      value={(editedRule as any)[field] || ''}
                      onChange={e => this.setState({ editedRule: { ...editedRule, [field]: e.target.value } })}
                    />
                  ) : (
                    <div style={{ fontSize: 13, color: T.textSecond, lineHeight: 1.55 }}>{(rule as any)[field] || '—'}</div>
                  )}
                </div>
              );
            })}

            {/* Technical details */}
            {(rule.vl || rule.td || rule.vc || rule.ve || rule.ex?.length || rule.exv?.length) && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, color: T.brand }}>
                  🔧 Technical Details
                </summary>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rule.vl && <div><div className="gw-label">Validation Logic</div><div style={{ fontSize: 12 }}>{rule.vl}</div></div>}
                  {rule.td && <div><div className="gw-label">Transform Detail</div><div style={{ fontSize: 12 }}>{rule.td}</div></div>}
                  {rule.vc && <div><div className="gw-label">SQL Check</div>
                    <pre style={{ background: T.bgSidebar, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, fontSize: 11, margin: 0 }}>{rule.vc}</pre></div>}
                  {rule.ve && <div><div className="gw-label">Validation Expression</div>
                    <pre style={{ background: T.bgSidebar, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, fontSize: 11, margin: 0 }}>{JSON.stringify(rule.ve, null, 2)}</pre></div>}
                  {rule.ex?.length && (
                    <div><div className="gw-label">Examples</div>
                      <ul style={{ fontSize: 12, paddingLeft: 16, margin: 0 }}>
                        {rule.ex.map((e: any, i: number) => (
                          <li key={i}><em>Before:</em> {JSON.stringify(e.before)} → <em>After:</em> {JSON.stringify(e.after)}{e.note && ` (${e.note})`}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Impact / validation results */}
            {vrs.length > 0 && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, color: T.brand }}>
                  📊 Impact ({vrs.length} result{vrs.length !== 1 ? 's' : ''})
                </summary>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {vrs.map((vr, i) => (
                    <div key={i} style={{ background: T.bgSidebar, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12 }}>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span className={cls('gw-badge', vr.status === 'PASS' ? 'gw-badge-green' : 'gw-badge-red')}>{vr.status}</span>
                        <span style={{ fontSize: 12, color: T.textMuted }}>
                          {vr.dimension === 'Conformity'
                            ? `${vr.examples?.affected_count ?? 0} applied / ${vr.total_records} total`
                            : `${vr.fail_count} failed / ${vr.passed_count} passed / ${vr.total_records} total`}
                        </span>
                      </div>
                      {vr.sample_failures?.length && (
                        <pre style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 6, padding: 8, fontSize: 11, maxHeight: 160, overflow: 'auto', margin: 0 }}>
                          {JSON.stringify(vr.sample_failures, null, 2)}
                        </pre>
                      )}
                      {vr.examples?.sample_changes?.slice(0, 5).map((ch: any, j: number) => (
                        <div key={j} style={{ fontSize: 12, marginTop: 4 }}>
                          {Object.entries(ch.field_changes as Record<string, { before: any; after: any }>).map(([fname, diff]) => (
                            <div key={fname}>
                              <strong>#{ch.index}:</strong>{' '}
                              <span style={{ color: T.red }}>{String((diff as any)?.before ?? '')}</span>
                              {' → '}
                              <span style={{ color: T.green }}>{String((diff as any)?.after ?? '')}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Edit save/cancel */}
            {isEditing && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                <button className="gw-btn gw-btn-ghost gw-btn-sm"
                  onClick={() => this.setState({ editingRuleId: null, editedRule: {} })}>Cancel</button>
                <button className="gw-btn gw-btn-primary gw-btn-sm" onClick={this.handleSaveEdit}>💾 Save</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Modeling recommendations tab ───────────────────────────────────────────
  renderModelingTab() {
    const { insight, editingModelingIndex, editedModeling } = this.state;
    const recs = insight.modeling_recommendations || [];
    if (!recs.length) return (
      <div className="gw-empty"><div className="gw-empty-icon">🔧</div><div className="gw-empty-label">No modeling recommendations.</div></div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {recs.map((rec: any, i: number) => {
          const isEditing = editingModelingIndex === i;
          const cur = isEditing ? editedModeling : rec;
          return (
            <div key={i} className="gw-card">
              <div className="gw-card-hdr">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="gw-badge gw-badge-muted">{cur.tag}</span>
                  <span className="gw-badge gw-badge-amber">{cur.priority}</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="gw-btn gw-btn-success gw-btn-sm" onClick={() => this.handleApproveModeling(i)}>✓ Approve</button>
                  <button className="gw-btn gw-btn-outline gw-btn-sm" onClick={() => this.handleEditModeling(i)}>✎ Edit</button>
                  <button className="gw-btn gw-btn-danger gw-btn-sm" onClick={() => this.handleRejectModeling(i)}>✖ Remove</button>
                </div>
              </div>
              <div className="gw-card-body">
                <div className="gw-label" style={{ marginBottom: 4 }}>Recommendation</div>
                {isEditing ? (
                  <textarea className="gw-textarea" value={editedModeling.recommendation || ''} rows={2}
                    onChange={e => this.setState({ editedModeling: { ...editedModeling, recommendation: e.target.value } })} />
                ) : (
                  <div style={{ fontSize: 13, marginBottom: 10 }}>{cur.recommendation}</div>
                )}
                <div className="gw-label" style={{ marginBottom: 4 }}>Rationale</div>
                {isEditing ? (
                  <textarea className="gw-textarea" value={editedModeling.rationale || ''} rows={2}
                    onChange={e => this.setState({ editedModeling: { ...editedModeling, rationale: e.target.value } })} />
                ) : (
                  <div style={{ fontSize: 13, color: T.textMuted }}>{cur.rationale}</div>
                )}
                {isEditing && (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                    <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={() => this.setState({ editingModelingIndex: null })}>Cancel</button>
                    <button className="gw-btn gw-btn-primary gw-btn-sm" onClick={this.handleSaveEditModeling}>💾 Save</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── DQ category tab content ────────────────────────────────────────────────
  renderCatTab(catKey: string) {
    const { insight, filterStatus } = this.state;
    const rules = (insight.validation_rules || [])
      .filter(r => (r.cat || 'other') === catKey)
      .filter(r => filterStatus === 'All' || (r.lifecycle || 'Draft') === filterStatus);

    return (
      <div>
        {this.renderFilterBar(catKey)}
        {rules.length === 0 ? (
          <div className="gw-empty" style={{ padding: '24px 0' }}>
            <div className="gw-empty-icon">📋</div>
            <div className="gw-empty-label">No rules match filter <strong>{filterStatus}</strong>.</div>
          </div>
        ) : (
          rules.map(rule => this.renderRuleCard(rule, catKey))
        )}
      </div>
    );
  }

  // ── Lookup modal (kept as Bootstrap Modal — complex enough to keep) ─────────
  renderLookupModal() {
    const { showLookupModal, lookupModalData } = this.state;
    if (!showLookupModal || !lookupModalData) return null;
    const { lkpPairs = [], selectedLookup } = lookupModalData;
    return (
      <Modal show={showLookupModal} onHide={() => this.setState({ showLookupModal: false })} size="lg" centered>
        <Modal.Header closeButton style={{ background: T.bgSidebar, borderBottom: `1px solid ${T.border}` }}>
          <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>
            Approve Lookup — {lookupModalData.columnName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: T.bg }}>
          <div className="gw-form-group" style={{ marginBottom: 16 }}>
            <label className="gw-label">Select Lookup RDS Name</label>
            <select className="gw-select"
              value={selectedLookup?.code || ''}
              onChange={e => {
                const sel = lkpPairs.find((l: any) => l.code === e.target.value);
                this.setState({ lookupModalData: { ...lookupModalData, selectedLookup: sel } });
              }}>
              {lkpPairs.map((l: any) => (
                <option key={l.code} value={l.code}>{l.rdsLabel || l.code} ({l.lookupValuesCnt})</option>
              ))}
            </select>
          </div>
          {lookupModalData.selectedLookup && (
            <div className="gw-card">
              <table className="gw-table">
                <thead><tr><th>Code</th><th>Description</th><th>Extra Info</th></tr></thead>
                <tbody>
                  {lookupModalData.selectedLookup.lookupValues.map((v: any, i: number) => (
                    <tr key={i}><td>{v.c}</td><td>{v.d}</td><td>{v.sc || v.sd}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: T.bgSidebar, borderTop: `1px solid ${T.border}` }}>
          <button className="gw-btn gw-btn-ghost gw-btn-sm"
            onClick={() => this.setState({ showLookupModal: false })}>Cancel</button>
          <button className="gw-btn gw-btn-primary gw-btn-sm"
            onClick={() => { this.props.onShouldBeLookupUpdate(lookupModalData.selectedLookup); this.setState({ showLookupModal: false }); }}>
            Submit
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    const { activeTab, showRuleTuning, tuningContext } = this.state;
    const tabs = this.buildTabs();

    return (
      <div className="gw-root">

        {/* ── Generate rules button ── */}
        <div style={{ marginBottom: 14 }}>
          <button className="gw-btn gw-btn-primary gw-btn-sm"
            onClick={() => {
              const f = this.props.profilingData;
              const summary = {
                column_name: f.column_name, orig_column_name: f.orig_column_name, source_prefix: f.source_prefix,
                field_profile: { data_type: f.data_type, count: f.count, not_null_count: f.not_null_count,
                  null_count: f.null_count, density: f.data_density,
                  distinct_values: Object.entries(f.distinct_values || {}).slice(0, 15) },
              };
              this.setState({ showRuleTuning: true, tuningContext: { type: 'field', fieldObj: summary, ruleObj: null } });
            }}>
            ✨ Add / Generate Field Rules
          </button>
        </div>

        {/* ── Tab nav ── */}
        <div className="gw-tabs" style={{ marginBottom: 0 }}>
          {tabs.map(tab => (
            <button key={tab.key}
              className={cls('gw-tab', activeTab === tab.key && 'active')}
              onClick={() => this.setState({ activeTab: tab.key })}>
              <span className="gw-tab-icon">{tab.icon}</span>
              {tab.label}
              <span className={cls('gw-badge', activeTab === tab.key ? 'gw-badge-brand' : 'gw-badge-muted')}
                style={{ marginLeft: 4 }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div style={{ padding: '16px 0', maxHeight: 620, overflowY: 'auto' }} className="gw-scroll">
          {activeTab === 'modeling'
            ? this.renderModelingTab()
            : this.renderCatTab(activeTab)
          }
        </div>

        {this.renderLookupModal()}

        {showRuleTuning && (
          <DQRuleTuningAssistant
            show={showRuleTuning}
            onHide={() => this.setState({ showRuleTuning: false, tuningContext: null })}
            dataModelData={this.props.dataModelData}
            appNm={this.props.selectedApp.nm}
            businessContext={this.props.selectedApp?.business_context || ''}
            entity={this.props.selectedApp?.setup[0]?.entity || ''}
            domain={this.props.selectedApp?.setup[0]?.domain || ''}
            fieldObj={tuningContext?.fieldObj}
            ruleContext={tuningContext?.type === 'rule' ? tuningContext.ruleObj : undefined}
            onRuleAccept={this.handleRuleAccept}
          />
        )}
      </div>
    );
  }
}